import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PortalPageHeader } from "@/components/portal/PortalLayout";
import { StatBadge } from "@/components/admin/AdminLayout";
import { Ticket, Euro, Users, TrendingUp, Link2, Copy, ExternalLink } from "lucide-react";
import PricingSimulator from "@/components/portal/PricingSimulator";
import { toast } from "sonner";

const PUBLIC_HOST = "ditours.pt";  // domínio público de produção

export default function PartnerDashboard() {
  const [me, setMe] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [statements, setStatements] = useState([]);

  useEffect(() => {
    api.get("/partner/me").then((r) => setMe(r.data));
    api.get("/partner/bookings").then((r) => setBookings(r.data));
    api.get("/partner/statements").then((r) => setStatements(r.data));
  }, []);

  if (!me) return <div className="p-10 text-slate-500">Loading…</div>;

  const totalCommission = bookings.reduce((s, b) => s + (b.partner_earning || b.partner_fee || 0), 0);
  const paidBookings = bookings.filter(b => b.payment_status === "paid").length;
  const partnerSlug = me.slug || me.id;
  const publicUrl = `https://${PUBLIC_HOST}/book/${partnerSlug}`;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copiado para o clipboard");
    } catch {
      toast.error("Não foi possível copiar. Selecione o texto manualmente.");
    }
  };

  return (
    <div className="p-10 max-w-[1400px]">
      <PortalPageHeader eyebrow={me.company_name} title="Dashboard" description="Overview of bookings, commissions and monthly statements." />

      {/* Msg 695: link do subdomínio público do parceiro */}
      <div className="mb-6 border-2 border-[#f59e0b] bg-gradient-to-br from-[#fff8ec] to-white p-5" data-testid="partner-public-url-card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="w-11 h-11 border-2 border-[#f59e0b] flex items-center justify-center flex-shrink-0">
            <Link2 className="w-5 h-5 text-[#f59e0b]" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-bold mb-1">Seu link público</div>
            <div className="font-mono text-sm md:text-base text-[#0a0f1e] font-bold break-all" data-testid="partner-public-url">
              {publicUrl}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Partilhe este link com clientes ou coloque-o no seu site. Os seus preços aparecem automaticamente.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={copyUrl}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#f59e0b] text-[#f59e0b] hover:bg-[#f59e0b] hover:text-white text-xs uppercase tracking-widest font-bold transition-colors"
              data-testid="copy-public-url-btn"
            >
              <Copy className="w-3.5 h-3.5" /> Copiar
            </button>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#f59e0b] text-white text-xs uppercase tracking-widest font-bold hover:bg-[#d97706] transition-colors"
              data-testid="open-public-url-btn"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Abrir
            </a>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-5 mb-8">
        <Kpi label="Total bookings" value={bookings.length} icon={Ticket} />
        <Kpi label="Paid" value={paidBookings} icon={Users} />
        <Kpi label="Commission earned" value={`€${totalCommission.toFixed(2)}`} icon={Euro} accent />
        <Kpi label="Commission %" value={`${me.commission_pct}%`} icon={TrendingUp} />
      </div>

      {/* Iter 47 (Msg 691): Pricing simulator — see earnings across commission tiers. */}
      <div className="mb-8">
        <PricingSimulator platformFeePct={me.effective_ditours_fee_pct ?? me.global_ditours_fee_pct ?? 15} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 p-6 shadow-sm">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-4">Recent bookings</p>
          <div className="space-y-3">
            {bookings.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No bookings yet.</div>}
            {bookings.slice(0, 6).map((b) => (
              <div key={b.id} data-testid={`p-booking-${b.id}`} className="flex items-center justify-between py-2 border-b border-slate-100">
                <div>
                  <div className="text-sm text-[#0a0f1e]">{b.client_name}</div>
                  <div className="text-xs text-slate-500 font-mono">{b.reference} · {new Date(b.service_datetime).toLocaleDateString()}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-mono text-[#0a0f1e]">€{b.client_pays?.toFixed(2) || b.total_amount?.toFixed(2)}</div>
                  <StatBadge status={b.status} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-6 shadow-sm">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-4">Monthly commission</p>
          <div className="space-y-3">
            {statements.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No statements yet.</div>}
            {statements.map((s) => (
              <div key={s.month} data-testid={`stmt-${s.month}`} className="flex items-center justify-between py-3 border-b border-slate-100">
                <div>
                  <div className="text-sm text-[#0a0f1e]">{s.month}</div>
                  <div className="text-xs text-slate-500">{s.bookings} bookings · €{s.revenue.toFixed(2)} gross</div>
                </div>
                <div className="font-display text-xl text-[#f59e0b]">€{s.commission.toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className={`bg-white border p-6 shadow-sm ${accent ? "border-[#f59e0b]" : "border-slate-200"}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
        <Icon className={`w-4 h-4 ${accent ? "text-[#f59e0b]" : "text-slate-400"}`} strokeWidth={1.5} />
      </div>
      <div className={`font-display text-3xl ${accent ? "text-[#f59e0b]" : "text-[#0a0f1e]"}`}>{value}</div>
    </div>
  );
}
